/**
 * Что показывает клетка матрицы истории и что с ней можно сделать (AK-772).
 *
 * Чистые функции: вся развилка «пусто / заполнено / правлено вручную / заперто»
 * решается ЗДЕСЬ и покрыта тестами, а разметка только рисует. Три правила
 * founder 31.08, которые легко потерять в JSX:
 *
 * 1. **«Пар» — заполненное значение, а не пустая клетка.** Поле с честным
 *    паром разобрано и в «осталось разобрать» не попадает.
 * 2. **Дозаполнить пустой год может любой, кто правит посевы; перезаписать
 *    заполненный — только руководитель.** Замочек показываем СРАЗУ, до
 *    попытки: «толкай и жди 403» — это усталость от системы.
 * 3. **Текущий сезон здесь не правится.** Живой год ведётся в карточке поля
 *    (`PUT rotation` отбивает его 409). Клетка честно говорит почему, а не
 *    молча роняет ошибку.
 */
import type { CropHistoryGrid, HistoryCell, HistoryFieldRow } from './api';

export type CellState = 'empty' | 'crop' | 'fallow';

export interface CellView {
  year: number;
  state: CellState;
  /** Что написать в клетке. */
  label: string;
  yieldText: string | null;
  manual: boolean;
  /** Год спорный — несколько занятий; правится в карточке поля. */
  disputed: boolean;
  /** Можно ли открыть форму правки. */
  editable: boolean;
  /** Почему нельзя — текст подсказки. `null`, когда можно. */
  lockReason: string | null;
}

export const CURRENT_YEAR_LOCK =
  'Текущий сезон ведётся в карточке поля, на вкладке «Посевы» — здесь заводится история прошлых лет.';

export const LEADER_LOCK =
  'Год уже заполнен. Изменить может руководитель хозяйства — напишите ему или попросите доступ.';

export const DISPUTED_LOCK =
  'В этом году на поле несколько занятий — разберите год в карточке поля.';

export function buildCellView({
  year,
  cell,
  canOverwrite,
  currentYear,
}: {
  year: number;
  cell: HistoryCell | undefined;
  canOverwrite: boolean;
  currentYear: number;
}): CellView {
  const state: CellState = !cell ? 'empty' : cell.kind === 'fallow' ? 'fallow' : 'crop';
  const disputed = (cell?.sowings_count ?? 0) > 1;

  let lockReason: string | null = null;
  if (year >= currentYear) lockReason = CURRENT_YEAR_LOCK;
  else if (disputed) lockReason = DISPUTED_LOCK;
  else if (cell && !canOverwrite) lockReason = LEADER_LOCK;

  return {
    year,
    state,
    label: !cell ? 'не заполнено' : cell.label,
    // Урожайности у пара не бывает; у культуры без цифры честно говорим «нет
    // данных» — это не ноль.
    yieldText:
      !cell || cell.kind === 'fallow'
        ? null
        : cell.yield_c_ha === null
          ? 'нет данных'
          : `${cell.yield_c_ha.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} ц/га`,
    manual: cell?.manual ?? false,
    disputed,
    editable: lockReason === null,
    lockReason,
  };
}

export function rowCells(
  row: HistoryFieldRow,
  years: number[],
  canOverwrite: boolean,
  currentYear: number,
): CellView[] {
  const byYear = new Map(row.cells.map((c) => [c.year, c]));
  return years.map((y) =>
    buildCellView({ year: y, cell: byYear.get(y), canOverwrite, currentYear }),
  );
}

/** Подпись прогресса шага: заполнено / осталось / поля без истории вовсе. */
export function progressText(grid: CropHistoryGrid): string {
  const left = grid.cells_total - grid.cells_filled;
  const pct = grid.cells_total === 0 ? 100 : Math.round((grid.cells_filled / grid.cells_total) * 100);
  const parts = [
    `История заполнена на ${pct} %`,
    `${grid.cells_filled} клеток из ${grid.cells_total}`,
    left > 0 ? `осталось разобрать ${left}` : 'разбирать нечего',
  ];
  if (grid.fields_without_history > 0) {
    parts.push(`${grid.fields_without_history} полей без истории вовсе`);
  }
  return parts.join(' · ');
}

export function progressPct(grid: CropHistoryGrid): number {
  if (grid.cells_total === 0) return 100;
  return Math.min(100, Math.round((grid.cells_filled / grid.cells_total) * 100));
}
